import React, { FC, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import {
    Image,
    Flag,
    Tag,
    Icon,
    Text,
    IconAndText,
    ButtonWithIcon
} from '@blueprint/ui';
import { Props } from './ListingCard.types';
import {
    mapPropertyImageResponseToGalleryImages,
    mapPropertyImageResponseToSrc
} from './helpers/responseImgConverter';
import GalleryLazyLoader from '../GalleryLazyLoader/GalleryLazyLoader';
import {
    TransportWrapper,
    StyledWrapper,
    ImageWrapper,
    DateDetailWrapper,
    ViewCountContainer,
    StyledContent,
    JustAddedFlag,
    StyledHelpToBuy,
    WrapperFeatures,
    StyledAddress,
    CardFooter,
    CardFooterCell,
    Wrapper,
    CardHeader,
    PriceContainer,
    AgencyLogoLink,
    MediaLinksContainer,
    ImageContainer,
    ImageCenterWrapper,
    SubImageContainer,
    SubImageWrapper,
    ImageLink,
    AvailableFromContainer,
    StyledTag,
    PriceDropText,
    PriceTitleText,
    ContactLink,
    StyledLink
} from './ListingCard.styles';

import { useRedirectWithSearchIndentifier } from './hooks/useRedirectWithSearchIndentifier';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import {
    hoverImageTracking,
    nextImageTracking,
    previousImageTracking
} from 'search/Listings/Listings.tracking';

const SUB_IMAGES_NUMBER = 4;
const SUB_IMAGES_INDEX = [0, 1, 2, 3];

const DEFAULT_IMAGE_ALT_TEXT = 'Thumbnail';
const getImageAltText = (props: Props) => {
    const { title, address } = props;

    if (title && address) {
        return `${title} in ${address}`;
    }

    return DEFAULT_IMAGE_ALT_TEXT;
};

// Aspect ratio of the thumbnail image is 69:50
const THUMBNAIL_IMAGE_INITIAL_WIDTH = 69;
const THUMBNAIL_IMAGE_INITIAL_HEIGHT =
    THUMBNAIL_IMAGE_INITIAL_WIDTH * (50 / 69);

// Aspect ratio of the agency logo image is 2:1
const AGENCY_LOGO_IMAGE_INITIAL_WIDTH = 73;
const AGENCY_LOGO_IMAGE_INITIAL_HEIGHT =
    AGENCY_LOGO_IMAGE_INITIAL_WIDTH * (1 / 2);

const ListingCardComponent: FC<Props> = (props) => {
    const {
        listingId,
        numberOfVideos,
        numberOfImages,
        numberOfFloorPlans,
        flag,
        isHelptoBuy,
        isPremium,
        underOffer = false,
        priceDrop,
        otherPropertyImages,
        priceTitle,
        price,
        branch,
        features,
        title,
        address,
        transports,
        highlights,
        tags,
        publishedOn,
        publishedOnLabel,
        numberOfViews,
        viewsLabel,
        viewsPeriodLabel,
        saveListing,
        contactOnClick,
        phoneOnClick,
        agentLogoOnClick,
        imageOnClick,
        cardOnClick,
        onCardEcommerceClick,
        titleOnClick,
        priceOnClick,
        descriptionOnClick,
        specOnClick,
        transportInfoOnClick,
        isFavourite,
        removeFavouriteLabel,
        saveFavouriteLabel,
        listingUris,
        image,
        searchIdentifier,
        availableFrom,
        availableFromLabel,
        'data-testid': dataTestId,
        searchType = 'for-sale',
        overrideViewsWithAvailability = false,
        useLazyImage = true,
        className
    } = props;

    const imageAlt = getImageAltText(props);

    const detailsLink = listingUris?.detail ? `${listingUris?.detail}` : '#';

    const availableFeatures = features.filter((feature) => feature.content > 0);

    const contactLink = listingUris?.contact?.replace(
        'contact',
        'details/contact'
    );

    const redirectToListing = useRedirectWithSearchIndentifier({
        searchIdentifier,
        listingUris,
        listingId
    });

    const mainImages = useMemo(
        () =>
            mapPropertyImageResponseToGalleryImages(
                image,
                otherPropertyImages || []
            ),
        [image, otherPropertyImages]
    );

    const premiumImages = useMemo(
        () => mapPropertyImageResponseToSrc(image, otherPropertyImages || []),
        [image, otherPropertyImages]
    );

    const normalizeSlideIndex = (index: number) => {
        const absoluteIndex = index < 0 ? index + mainImages.length : index;
        const roundedIndex =
            mainImages.length === 0 ? 0 : absoluteIndex % mainImages.length;
        return roundedIndex;
    };

    const [slideIndex, setSlideIndex] = useState(0);
    const [subImageIndex, setSubImageIndex] = useState(0);

    const subImageIndexs = SUB_IMAGES_INDEX.map((index) =>
        normalizeSlideIndex(subImageIndex + index)
    );
    const selectedSubImage = normalizeSlideIndex(slideIndex - subImageIndex);

    const changeSlideHandler = (index: number) => {
        const normalizedIndex = normalizeSlideIndex(index);
        setSlideIndex(normalizedIndex);
    };

    const nextSlideHandler = (index: number) => {
        nextImageTracking(listingId);
        const normalizedIndex = normalizeSlideIndex(index + 1);
        if (!subImageIndexs.includes(normalizedIndex))
            setSubImageIndex(normalizeSlideIndex(subImageIndex + 1));
    };

    const previousSlideHandler = (index: number) => {
        previousImageTracking(listingId);
        const normalizedIndex = normalizeSlideIndex(index - 1);
        if (!subImageIndexs.includes(normalizedIndex))
            setSubImageIndex(normalizeSlideIndex(subImageIndex - 1));
    };

    const getPremiumImages = () => {
        if (premiumImages.length <= SUB_IMAGES_NUMBER) return premiumImages;

        return subImageIndexs.map((index) => premiumImages[index]);
    };

    const displayPremiumImages =
        isPremium && premiumImages ? getPremiumImages() : [];

    const [isMobile] = useIsWidthLessThanBreakpoint([786]);

    return (
        <Wrapper data-testid={dataTestId} className={className}>
            <StyledWrapper>
                <ImageWrapper>
                    <ImageContainer>
                        <ImageCenterWrapper>
                            <ImageLink
                                data-testid="listing-details-image-link"
                                href={detailsLink}
                                onClick={(event) => {
                                    event.preventDefault();
                                }}
                            >
                                <GalleryLazyLoader
                                    updateSlideIndex={slideIndex}
                                    initialSlide={0}
                                    images={mainImages}
                                    allowCropping={true}
                                    createCaption={(index, total) =>
                                        index === 0
                                            ? imageAlt
                                            : `Property photo ${
                                                  index + 1
                                              } of ${total}. ${
                                                  mainImages[index].alt ?? ''
                                              }`
                                    }
                                    onSlideClick={() => {
                                        onCardEcommerceClick &&
                                            onCardEcommerceClick();
                                        imageOnClick && imageOnClick();
                                        redirectToListing();
                                    }}
                                    hasKeyboardControls={false}
                                    hasArrowsOnImage={true}
                                    hideProgressIndicator={true}
                                    onSlideChanged={changeSlideHandler}
                                    onSlideNext={nextSlideHandler}
                                    onSlidePrevious={previousSlideHandler}
                                    onHoverOnce={() =>
                                        hoverImageTracking(listingId)
                                    }
                                    hasClickDragging={isMobile}
                                    lazyLoad={useLazyImage}
                                />
                            </ImageLink>
                        </ImageCenterWrapper>
                        <MediaLinksContainer>
                            {numberOfVideos > 0 && (
                                <Tag>
                                    <Icon
                                        iconId="play"
                                        color="white"
                                        size="small"
                                    />
                                    {numberOfVideos}
                                </Tag>
                            )}
                            {numberOfImages > 0 && (
                                <Tag>
                                    <Icon
                                        iconId="camera"
                                        color="white"
                                        size="small"
                                    />
                                    {`${slideIndex + 1}/${numberOfImages}`}
                                </Tag>
                            )}
                            {numberOfFloorPlans > 0 && (
                                <Tag>
                                    <Icon
                                        iconId="floorplan"
                                        color="white"
                                        size="small"
                                    />
                                    {numberOfFloorPlans}
                                </Tag>
                            )}
                        </MediaLinksContainer>
                        {flag && (
                            <JustAddedFlag variant="justAdded">
                                {flag}
                            </JustAddedFlag>
                        )}
                        {isHelptoBuy && <StyledHelpToBuy />}
                    </ImageContainer>
                    {displayPremiumImages.length > 0 && (
                        <SubImageWrapper data-testid="premium-listing-images">
                            {displayPremiumImages.map((item, index) => {
                                const { caption, small } = item;
                                const title = caption ? caption : '';
                                return (
                                    <SubImageContainer
                                        key={`${small}-${index}`}
                                        data-testid={`listing-subimage-${index}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSlideIndex(
                                                normalizeSlideIndex(
                                                    subImageIndexs[index]
                                                )
                                            );
                                        }}
                                        src={small}
                                        width={THUMBNAIL_IMAGE_INITIAL_WIDTH}
                                        height={THUMBNAIL_IMAGE_INITIAL_HEIGHT}
                                        title={title}
                                        alt={`${address}. Sub image ${index}. ${title}`}
                                        selected={selectedSubImage == index}
                                    />
                                );
                            })}
                        </SubImageWrapper>
                    )}
                </ImageWrapper>
                <StyledContent>
                    <AgencyLogoLink
                        plain
                        href={branch.branchDetailsUri}
                        data-testid="listing-details-agent-logo"
                        onClick={agentLogoOnClick}
                    >
                        {branch.logoUrl === 'false' ? (
                            <Text data-testid="listing-details-agent-name">
                                Marketed by {branch.name}
                            </Text>
                        ) : (
                            <Image
                                src={branch.logoUrl}
                                alt={`Marketed by ${branch.name}`}
                                height={AGENCY_LOGO_IMAGE_INITIAL_HEIGHT}
                                width={AGENCY_LOGO_IMAGE_INITIAL_WIDTH}
                            />
                        )}
                    </AgencyLogoLink>

                    <StyledLink
                        plain
                        href={detailsLink}
                        data-testid="listing-details-link"
                        onClick={(event) => {
                            event.preventDefault();
                            onCardEcommerceClick && onCardEcommerceClick();
                            redirectToListing();
                            if (event.target === event.currentTarget) {
                                cardOnClick && cardOnClick();
                            }
                        }}
                    >
                        <CardHeader>
                            <PriceContainer
                                data-testid="listing-price"
                                onClick={priceOnClick}
                            >
                                {isPremium && (
                                    <Flag
                                        data-testid="premium-listing"
                                        variant="premium"
                                    >
                                        Premium listing
                                    </Flag>
                                )}
                                {underOffer && (
                                    <Flag
                                        data-testid="under-offer"
                                        variant="underOffer"
                                    >
                                        Under offer
                                    </Flag>
                                )}
                                {priceTitle && (
                                    <PriceTitleText size="secondary">
                                        {priceTitle}
                                    </PriceTitleText>
                                )}
                                <Text size="6" isBold>
                                    {price}
                                </Text>
                                {priceDrop && (
                                    <PriceDropText
                                        size="secondary"
                                        color="grey-slate"
                                        data-testid="price-drop-label"
                                    >
                                        <Icon
                                            iconId="arrow_full_down"
                                            color="crimson"
                                            size="medium"
                                        />{' '}
                                        <strong>
                                            {priceDrop.percentageChangeLabel?.replace(
                                                '.0',
                                                ''
                                            )}
                                            %
                                        </strong>{' '}
                                        - {priceDrop.lastPriceChangeDate}
                                    </PriceDropText>
                                )}
                            </PriceContainer>
                        </CardHeader>
                        <WrapperFeatures
                            data-testid="listing-spec"
                            onClick={specOnClick}
                        >
                            {availableFeatures.map((item, index) => {
                                const { iconId, content } = item;

                                return (
                                    <IconAndText
                                        isInline
                                        key={`${index}-${iconId}`}
                                        iconId={iconId}
                                        content={content}
                                        iconColor={'grey-steel'}
                                        contentColor={'grey-slate'}
                                        size="medium"
                                    />
                                );
                            })}
                        </WrapperFeatures>

                        {title && (
                            <StyledAddress
                                data-testid="listing-title"
                                size="8"
                                color={'text-title'}
                                onClick={titleOnClick}
                            >
                                {title}
                            </StyledAddress>
                        )}
                        <Text
                            data-testid="listing-description"
                            size="secondary"
                            color={'grey-steel'}
                            onClick={descriptionOnClick}
                        >
                            {address}
                        </Text>

                        <TransportWrapper
                            data-testid="listing-transport"
                            onClick={transportInfoOnClick}
                        >
                            {transports.map((item, i: number) => (
                                <IconAndText
                                    isInline
                                    key={i}
                                    iconId={item.poiType}
                                    content={`${item.distanceInMiles} miles ${item.title}`}
                                    contentColor="grey-steel"
                                    size="small"
                                />
                            ))}
                            {isPremium &&
                                highlights &&
                                highlights.map((item, i) => (
                                    <IconAndText
                                        data-testid="premium-plus-feature"
                                        isInline
                                        key={i}
                                        iconId="tick"
                                        content={item.label}
                                        iconColor="primary-brand"
                                        contentColor="grey-steel"
                                        size="small"
                                    />
                                ))}
                        </TransportWrapper>
                        {tags &&
                            tags.map((item, i) => (
                                <StyledTag key={i}>{item.content}</StyledTag>
                            ))}
                    </StyledLink>
                </StyledContent>
            </StyledWrapper>
            <CardFooter>
                <DateDetailWrapper>
                    {publishedOn && (
                        <Text
                            as="span"
                            size="small"
                            color={'grey-steel'}
                            data-testid="date-published"
                        >
                            {publishedOnLabel} {publishedOn}
                        </Text>
                    )}
                    {numberOfViews > 0 && !overrideViewsWithAvailability && (
                        <ViewCountContainer>
                            <Text
                                as="span"
                                size="small"
                                isBold
                                color={'grey-steel'}
                                data-testid="number-of-views"
                            >
                                {`${numberOfViews} ${viewsLabel}`}
                            </Text>
                            <Text
                                as="span"
                                size="small"
                                color={'grey-steel'}
                                data-testid="number-of-views-label"
                            >
                                {`\u00A0${viewsPeriodLabel}`}
                            </Text>
                        </ViewCountContainer>
                    )}
                    {((numberOfViews === 0 &&
                        searchType === 'to-rent' &&
                        availableFrom) ||
                        overrideViewsWithAvailability) && (
                        <AvailableFromContainer>
                            <Text
                                as="span"
                                size="small"
                                color={'grey-steel'}
                                data-testid="available-from-label"
                            >
                                {availableFromLabel}
                            </Text>
                            <Text
                                as="span"
                                size="small"
                                isBold
                                color={'grey-steel'}
                                data-testid="available-from-date"
                            >
                                {`\u00A0${availableFrom}`}
                            </Text>
                        </AvailableFromContainer>
                    )}
                </DateDetailWrapper>

                <CardFooterCell>
                    {!!listingUris && (
                        <>
                            {!!branch?.phone && (
                                <ContactLink
                                    href={`tel:${branch.phone}`}
                                    onClick={phoneOnClick}
                                    variant="tertiary"
                                    data-testid="agent-phone-number"
                                >
                                    <Icon
                                        iconId="phone_outline"
                                        size="medium"
                                    />
                                    {branch.phone}
                                </ContactLink>
                            )}

                            {!!contactLink && (
                                <ContactLink
                                    href={contactLink}
                                    onClick={contactOnClick}
                                    variant="tertiary"
                                    data-testid="agent-contact-link"
                                >
                                    <Icon
                                        iconId="email_outline"
                                        size="medium"
                                    />
                                    Contact
                                </ContactLink>
                            )}
                        </>
                    )}
                    <ButtonWithIcon
                        variant="tertiary"
                        data-testid="save-listing"
                        onClick={saveListing}
                        aria-label={
                            isFavourite
                                ? removeFavouriteLabel
                                : saveFavouriteLabel
                        }
                    >
                        <Icon
                            iconId={
                                isFavourite ? 'save_filled' : 'save_outline'
                            }
                            size="medium"
                        />{' '}
                        {isFavourite ? 'Saved' : 'Save'}
                    </ButtonWithIcon>
                </CardFooterCell>
            </CardFooter>
        </Wrapper>
    );
};

/**
| **Team**               |       |
| -----------------------|--------------|
| **Zeroheight**         |   https://zeroheight.com/8f6f2b33a/v/latest/p/07d92d-listing-cards/b/346990   |
**/
const ListingCard = styled(ListingCardComponent)();
export default ListingCard;
